import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';

export interface WebsiteHostingMap {
  id: string;
  user_id: string;
  website_id: string;
  hosting_id: string;
  notes?: string;
  created_at: string;
  updated_at: string;
  // Joined data
  website_name?: string;
  hosting_name?: string;
  website_url?: string;
  hosting_panel_url?: string;
  /** hostings.ip_address — 'EMPTY' albo pusty gdy brak (kreator Przenieś domeny: IP dla rekordu A). */
  hosting_ip_address?: string;
}

export interface NewWebsiteHostingMap {
  website_id: string;
  hosting_id: string;
  notes?: string;
}

export const useWebsiteHostingMap = () => {
  const [mappings, setMappings] = useState<WebsiteHostingMap[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();
  const { toast } = useToast();

  // Fetch mappings with joined data
  const fetchMappings = useCallback(async () => {
    if (!user) return;

    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('website_hosting_map')
        .select(`
          *,
          websites!inner(name, url),
          hostings!inner(name, panel_url, ip_address)
        `);

      if (error) throw error;

      // Transform data to include joined fields
      const transformedData = data?.map(item => ({
        ...item,
        website_name: item.websites?.name,
        hosting_name: item.hostings?.name,
        website_url: item.websites?.url,
        hosting_panel_url: item.hostings?.panel_url,
        hosting_ip_address: item.hostings?.ip_address ?? undefined,
      })) || [];

      setMappings(transformedData);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Błąd podczas pobierania mapowań';
      setError(errorMessage);
      toast({
        title: "Błąd",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [user?.id, toast]);

  // Create new mapping
  const createMapping = useCallback(async (mappingData: NewWebsiteHostingMap): Promise<boolean> => {
    if (!user) {
      toast({
        title: "Błąd",
        description: "Musisz być zalogowany",
        variant: "destructive",
      });
      return false;
    }

    try {
      const { error } = await supabase
        .from('website_hosting_map')
        .insert(mappingData);

      if (error) throw error;

      await fetchMappings(); // Refresh data
      toast({
        title: "Sukces",
        description: "Hosting został przypisany do domeny",
      });

      return true;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Błąd podczas tworzenia mapowania';
      toast({
        title: "Błąd",
        description: errorMessage,
        variant: "destructive",
      });
      return false;
    }
  }, [user, fetchMappings, toast]);

  // Delete mapping
  const deleteMapping = useCallback(async (id: string): Promise<boolean> => {
    try {
      const { error } = await supabase
        .from('website_hosting_map')
        .delete()
        .eq('id', id);

      if (error) throw error;

      await fetchMappings(); // Refresh data
      toast({
        title: "Sukces",
        description: "Przypisanie zostało usunięte",
      });

      return true;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Błąd podczas usuwania mapowania';
      toast({
        title: "Błąd",
        description: errorMessage,
        variant: "destructive",
      });
      return false;
    }
  }, [fetchMappings, toast]);

  // Get mappings for specific website
  const getMappingsForWebsite = useCallback((websiteId: string) => {
    return mappings.filter(mapping => mapping.website_id === websiteId);
  }, [mappings]);

  // Get mappings for specific hosting
  const getMappingsForHosting = useCallback((hostingId: string) => {
    return mappings.filter(mapping => mapping.hosting_id === hostingId);
  }, [mappings]);

  // Set up real-time subscription
  useEffect(() => {
    if (!user) return;

    const subscription = supabase
      .channel('website_hosting_map_changes')
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'website_hosting_map',
      }, () => {
        fetchMappings(); // Refresh data on any change
      })
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, [user?.id, fetchMappings]);

  // Initial fetch
  useEffect(() => {
    if (user?.id) {
      fetchMappings();
    }
  }, [user?.id, fetchMappings]);

  return {
    mappings,
    loading,
    error,
    createMapping,
    deleteMapping,
    getMappingsForWebsite,
    getMappingsForHosting,
    refreshMappings: fetchMappings,
  };
};
